import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { X, FileText, Upload } from 'lucide-react';
import { rawColors } from "../design-system/tokens/colors";

const TextareaContainer = styled.div<{ isDragActive: boolean }>`
  position: relative;
  border-radius: 12px;
  transition: all 0.3s ease;
  min-height: 3.5rem;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Textarea = styled.textarea<{ hasAnimatedPlaceholder: boolean }>`
  flex: 1;
  background: transparent;
  border: none;
  font-size: 1rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen",
    "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
    sans-serif;
  resize: none;
  outline: none;
  line-height: 1.4;
  min-height: 3.5rem;
  min-height: 80px;

  &::placeholder {
    color: ${props => props.hasAnimatedPlaceholder ? 'transparent' : rawColors.text.mediumEmphasis};
    font-family: "Plus Jakarta Sans";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const AnimatedPlaceholder = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  align-items: flex-start;
  padding-top: 8px; /* Adjust to match textarea padding */
  color: ${rawColors.text.mediumEmphasis};
  font-family: "Plus Jakarta Sans";
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.2s ease;
`;

const PlaceholderText = styled.span`
  display: inline-block;
`;

const AnimatedWord = styled.span`
  display: inline-block;
  min-width: 1ch; /* Ensures space for at least one character */
`;

const Cursor = styled.span<{ isBlinking: boolean }>`
  display: inline-block;
  width: 1px;
  height: 1em;
  background-color: ${rawColors.brand.primaryDarker};
  margin-left: 2px;
  animation: ${props => props.isBlinking ? 'blink 1s infinite' : 'none'};

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

const FilePill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 107, 157, 0.2);
  border: 1px solid rgba(255, 107, 157, 0.3);
  border-radius: 20px;
  padding: 0.2rem;
  padding-left: 10px;
  color: ${rawColors.text.white};
  font-size: 0.9rem;
  max-width: fit-content;
`;

const FileIcon = styled.div`
  display: flex;
  align-items: center;
  color: #ff6b9d;
`;

const FileName = styled.span`
  font-weight: 500;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    color: #ff4757;
    background: rgba(255, 71, 87, 0.1);
  }
`;

const DragOverlay = styled.div<{ isDragActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px dashed #2ed573;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2ed573;
  font-weight: 600;
  font-size: 1.1rem;
  opacity: ${props => props.isDragActive ? 1 : 0};
  pointer-events: ${props => props.isDragActive ? 'auto' : 'none'};
  transition: all 0.3s ease;
  z-index: 10;
`;

const DragContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const DragIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(46, 213, 115, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2ed573;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

interface DragDropTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  animatedWords?: string[];
  onFileSelect?: (file: File) => void;
  onFileRemove?: () => void;
  selectedFile?: File | null;
  disabled?: boolean;
  className?: string;
  fileInputRef?: React.RefObject<HTMLInputElement>;
  onKeyPress?: (e: React.KeyboardEvent) => void;
}

const DragDropTextarea: React.FC<DragDropTextareaProps> = ({
  value,
  onChange,
  placeholder = "Type your message...",
  animatedWords = [],
  onFileSelect,
  onFileRemove,
  selectedFile,
  disabled = false,
  className,
  fileInputRef: externalFileInputRef,
  onKeyPress
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const internalFileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = externalFileInputRef || internalFileInputRef;

  const hasAnimatedWords = animatedWords.length > 0;
  const basePlaceholder = placeholder.endsWith(' ') ? placeholder : `${placeholder} `;

  useEffect(() => {
    if (!hasAnimatedWords) return;

    const currentWord = animatedWords[currentWordIndex];
    let timeout: NodeJS.Timeout;

    if (isWaiting) {
      timeout = setTimeout(() => {
        setIsWaiting(false);
        setIsDeleting(true);
      }, 2000);
    } else if (isDeleting) {
      if (currentText.length > 0) {
        timeout = setTimeout(() => {
          setCurrentText(prev => prev.slice(0, -1));
        }, 50);
      } else {
        setIsDeleting(false);
        setCurrentWordIndex(prev => (prev + 1) % animatedWords.length);
      }
    } else {
      if (currentText.length < currentWord.length) {
        timeout = setTimeout(() => {
          setCurrentText(prev => currentWord.slice(0, prev.length + 1));
        }, 100);
      } else {
        setIsWaiting(true);
      }
    }

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, isWaiting, currentWordIndex, animatedWords, hasAnimatedWords]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      if (isValidFileType(file)) {
        onFileSelect?.(file);
      } else {
        alert('Please upload a valid file type (CSV, XLS, XLSX)');
      }
    }
  }, [onFileSelect]);

  const isValidFileType = (file: File): boolean => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const validExtensions = ['.csv', '.xls', '.xlsx'];
    
    return validTypes.includes(file.type) || 
           validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (isValidFileType(file)) {
        onFileSelect?.(file);
      } else {
        alert('Please upload a valid file type (CSV, XLS, XLSX)');
      }
    }
    e.target.value = '';
  };

  const handleRemoveFile = () => {
    onFileRemove?.();
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const showAnimatedPlaceholder = hasAnimatedWords && !value && !isDragActive;

  return (
    <TextareaContainer
      isDragActive={isDragActive}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={className}
    >
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyPress={onKeyPress}
        placeholder={showAnimatedPlaceholder ? `${basePlaceholder}${currentText}<Cursor isBlinking={!isDeleting && !isWaiting} />` : placeholder}
        disabled={disabled}
        hasAnimatedPlaceholder={hasAnimatedWords}
      />
      
      {showAnimatedPlaceholder && (
        <AnimatedPlaceholder isVisible={true}>
          <PlaceholderText>
            {basePlaceholder}
            <AnimatedWord>{currentText}</AnimatedWord>
            {/* <Cursor isBlinking={!isDeleting && !isWaiting} /> */}
          </PlaceholderText>
        </AnimatedPlaceholder>
      )}
      
      {/* {selectedFile && (
        <FilePill>
          <FileIcon>
            <FileText size={16} />
          </FileIcon>
          <FileName>{selectedFile.name}</FileName>
          <RemoveButton onClick={handleRemoveFile} title="Remove file">
            <X size={14} />
          </RemoveButton>
        </FilePill>
      )} */}

      <DragOverlay isDragActive={isDragActive}>
        <DragContent>
          <DragIcon>
            <Upload size={24} />
          </DragIcon>
          <div>Drop your file here</div>
        </DragContent>
      </DragOverlay>

      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={handleFileInputChange}
      />
    </TextareaContainer>
  );
};

export default DragDropTextarea;
